import {
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import {
  WorkItemList,
  WorkItemStateEnum,
  WorkItemTypeEnum,
} from "../../store/services/timelineApiRTK";
import {
  getTeamColor,
  getWorkItemColor,
  getWorkItemTypeName,
} from "../../utils/workItemUtils";
import { formatDate } from "../next-action/helpers";

interface WorkDetailsCardProps {
  activity: WorkItemList;
  onMoveClick: () => void;
  onWorkClick: () => void;
  onAssignClick: () => void; // Om du vill ha en funktion för att hantera tilldelning
}

const WorkDetailsCard: React.FC<WorkDetailsCardProps> = ({
  activity,
  onMoveClick,
  onWorkClick,
  onAssignClick, // Om du använder en dialog för att tilldela
}) => {
  const activityTypeColor = getWorkItemColor(activity.Type);
  const teamColor = getTeamColor(activity.Team?.Id);

  return (
    <Card elevation={0} sx={{ border: "1px solid", borderColor: "grey.200" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="medium">
          Uppgiftsdetaljer
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Chip
                label={
                  activity.State === WorkItemStateEnum.Active
                    ? "Aktiv"
                    : "Avslutad"
                }
                color={
                  activity.State === WorkItemStateEnum.Active
                    ? "success"
                    : "default"
                }
                variant="outlined"
              />
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Typ
              </Typography>
              <Chip
                label={getWorkItemTypeName(
                  activity.Type || WorkItemTypeEnum.PhoneCall
                )}
                sx={{
                  bgcolor: `${activityTypeColor}20`,
                  color: activityTypeColor,
                  borderColor: activityTypeColor,
                  borderWidth: 1,
                  borderStyle: "solid",
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Team
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <BusinessIcon fontSize="small" sx={{ color: teamColor }} />
                <Typography variant="body1">
                  {activity.Team?.Name || "Ej tilldelat"}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box
              sx={{ cursor: "pointer" }}
              onClick={onAssignClick}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Tilldelad till
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body1">
                  {activity.AssignedTo?.Name || "Ej tilldelad"}
                </Typography>
                <EditIcon fontSize="small" color="action" />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent={"space-between"}
              sx={{ cursor: "pointer" }}
              onClick={onMoveClick}
            >
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Schemalagd till
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="body1">
                    {activity.ScheduledTo
                      ? formatDate(activity.ScheduledTo)
                      : "Ej schemalagd"}
                  </Typography>
                </Box>
              </Box>
              <EditIcon fontSize="small" color="action" />
            </Stack>
          </Grid>

          <Grid item xs={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Relaterat ärende
              </Typography>
              {activity.WorkId ? (
                <Chip
                  label={`Ärende #${activity.WorkId}`}
                  color="primary"
                  variant="outlined"
                  clickable
                  onClick={onWorkClick}
                  icon={<WorkIcon />}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Ingen koppling
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default WorkDetailsCard;
